import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const articleLinks = [
  { title: "Beginner's Guide", slug: "how-to-use-mulebuy-spreadsheet" },
  { title: "Top Finds 2026", slug: "top-mulebuy-spreadsheet-finds-2026" },
  { title: "Spreadsheet vs Search", slug: "mulebuy-spreadsheet-vs-direct-search" },
  { title: "Verify Sellers", slug: "verify-mulebuy-sellers" },
  { title: "QC Guide", slug: "mulebuy-qc-guide" },
  { title: "Shipping Guide", slug: "international-shipping-mulebuy" },
  { title: "Sneakers Guide", slug: "mulebuy-sneakers-guide" },
  { title: "Safety Analysis", slug: "is-mulebuy-safe-2026" },
  { title: "2026 Updates", slug: "mulebuy-spreadsheet-updates-2026" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState<string | null>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileSubmenu(null);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md border-b border-background-200 shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo / Home */}
            <Link
              to="/"
              onClick={() => window.scrollTo(0, 0)}
              className={`flex items-center gap-2 group ${
                scrolled ? "" : ""
              }`}
            >
              <span className={`text-lg md:text-xl font-heading font-bold transition-colors duration-300 ${
                scrolled ? "text-foreground-950" : "text-white"
              }`}>
                Mulebuy<span className={scrolled ? "text-accent-600" : "text-accent-400"}> Spreadsheet</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                onClick={() => window.scrollTo(0, 0)}
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled
                    ? "text-foreground-800 hover:text-accent-600"
                    : "text-white/90 hover:text-white"
                }`}
              >
                Home
              </Link>

              <Link
                to="/mulebuy-spreadsheet-guide"
                onClick={() => window.scrollTo(0, 0)}
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled
                    ? "text-foreground-800 hover:text-accent-600"
                    : "text-white/90 hover:text-white"
                }`}
              >
                Shopping Guide
              </Link>

              {/* Articles Dropdown - Desktop */}
              <div className="relative group">
                <button
                  className={`flex items-center gap-1 text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                    scrolled
                      ? "text-foreground-800 hover:text-accent-600"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  SEO Articles
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </span>
                </button>
                <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-background-200 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-2">
                    {articleLinks.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        onClick={() => window.scrollTo(0, 0)}
                        className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-accent-700 transition-colors duration-150 whitespace-nowrap"
                      >
                        {article.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Categories Dropdown - Desktop */}
              <div className="relative group">
                <button
                  className={`flex items-center gap-1 text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                    scrolled
                      ? "text-foreground-800 hover:text-accent-600"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  Categories
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </span>
                </button>
                <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-background-200 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 max-h-72 overflow-y-auto">
                  <div className="py-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.name}
                        onClick={() => handleExternalLink(cat.url)}
                        className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-accent-700 transition-colors duration-150 whitespace-nowrap flex items-center justify-between"
                      >
                        {cat.name}
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden flex items-center"
              aria-label="Open menu"
            >
              <span className={`w-5 h-5 flex items-center justify-center ${scrolled ? "text-foreground-950" : "text-white"}`}>
                <i className="ri-menu-line text-xl"></i>
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeMobile}
          ></div>
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200">
              <span className="font-heading font-bold text-foreground-950 text-base">Menu</span>
              <button
                onClick={closeMobile}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>

            <div className="p-4 space-y-1">
              <Link
                to="/"
                onClick={() => { closeMobile(); window.scrollTo(0, 0); }}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150"
              >
                <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                  <i className="ri-home-line"></i>
                </span>
                Home
              </Link>

              <Link
                to="/mulebuy-spreadsheet-guide"
                onClick={() => { closeMobile(); window.scrollTo(0, 0); }}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150"
              >
                <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                  <i className="ri-book-open-line"></i>
                </span>
                Shopping Guide
              </Link>

              {/* Articles mobile */}
              <button
                onClick={() => setMobileSubmenu(mobileSubmenu === "articles" ? null : "articles")}
                className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150"
              >
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                    <i className="ri-article-line"></i>
                  </span>
                  SEO Articles
                </div>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileSubmenu === "articles" ? "rotate-180" : ""}`}></i>
                </span>
              </button>
              {mobileSubmenu === "articles" && (
                <div className="ml-8 space-y-1 border-l-2 border-background-200 pl-4 mb-2">
                  {articleLinks.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => { closeMobile(); window.scrollTo(0, 0); }}
                      className="block px-3 py-2 text-sm text-foreground-600 hover:text-accent-700 transition-colors duration-150 rounded-lg hover:bg-background-50 whitespace-nowrap"
                    >
                      {article.title}
                    </Link>
                  ))}
                </div>
              )}

              {/* Categories mobile */}
              <button
                onClick={() => setMobileSubmenu(mobileSubmenu === "categories" ? null : "categories")}
                className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150"
              >
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories
                </div>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileSubmenu === "categories" ? "rotate-180" : ""}`}></i>
                </span>
              </button>
              {mobileSubmenu === "categories" && (
                <div className="ml-8 space-y-1 border-l-2 border-background-200 pl-4 mb-2 max-h-60 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => {
                        handleExternalLink(cat.url);
                        closeMobile();
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-foreground-600 hover:text-accent-700 transition-colors duration-150 rounded-lg hover:bg-background-50 whitespace-nowrap flex items-center justify-between"
                    >
                      {cat.name}
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 mt-2 border-t border-background-200">
              <button
                onClick={() => {
                  handleExternalLink("https://kaspreadsheet.com");
                  closeMobile();
                }}
                className="w-full whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 bg-primary-500 text-white px-4 py-3 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors duration-200"
              >
                Visit Main Store
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}